import { useQuery } from '@tanstack/react-query';
import { Box, Group, Text, Center, Loader } from '@mantine/core';

import { gamesApi } from '../api';
import { Leaderboard, type LeaderboardRow } from './Leaderboard';
import { Eyebrow } from './Eyebrow';
import { numStyle } from '../theme';
import { formatDuration, plural } from '../utils/format';

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
      <Group p="sm" pb={4}><Eyebrow>{title}</Eyebrow></Group>
      {children}
    </Box>
  );
}

function Empty({ text }: { text: string }) {
  return <Text c="dimmed" fz="sm" px="md" pb="sm">{text}</Text>;
}

const TOP = 3;

/**
 * Stručný průběh běžící hry na přehledu. Vysílačky mají vlastní, bohatší zobrazení
 * (mapa, kola) — tady jde o ostatní módy, aby přehled neukazoval jen jejich název.
 */
export function GameQuickSummary({ mode, gameUuid }: { mode?: string | null; gameUuid: string }) {
  switch (mode) {
    case 'dominations': return <DominationsSummary gameUuid={gameUuid} />;
    case 'hostina':     return <HostinaSummary gameUuid={gameUuid} />;
    case 'pocitadlo':   return <PocitadloSummary gameUuid={gameUuid} />;
    case 'stopky':      return <StopkySummary gameUuid={gameUuid} />;
    case 'mrtvoliste':  return <MrtvolisteSummary gameUuid={gameUuid} />;
    default:            return null;
  }
}

function DominationsSummary({ gameUuid }: { gameUuid: string }) {
  const q = useQuery({
    queryKey: ['dominations', gameUuid],
    queryFn: () => gamesApi.getDominationsScore(gameUuid),
    refetchInterval: 20_000,
  });
  if (q.isLoading) return <Frame title="Skóre"><Center h={50}><Loader size="xs" /></Center></Frame>;
  if (q.error || !q.data) return null;

  const { totalBlackScore: black, totalWhiteScore: white, territories } = q.data;
  if (!black && !white && territories.length === 0) {
    return <Frame title="Skóre"><Empty text="Zatím se nebojovalo o žádné teritorium." /></Frame>;
  }
  const total = black + white || 1;

  return (
    <Frame title="Černí proti bílým">
      <Box px="md" pb="sm">
        <Box style={{
          display: 'flex', height: 26, borderRadius: 5, overflow: 'hidden',
          border: '1px solid var(--mantine-color-default-border)',
        }}>
          <Box style={{ flex: black / total, background: '#1B1B1B', color: '#fff',
            display: 'grid', placeItems: 'center', ...numStyle, fontWeight: 700, fontSize: 12 }}>
            {black}
          </Box>
          <Box style={{ flex: white / total, background: '#F5F5F2', color: '#1A1A1A',
            display: 'grid', placeItems: 'center', ...numStyle, fontWeight: 700, fontSize: 12 }}>
            {white}
          </Box>
        </Box>
        <Group justify="space-between" mt={5}>
          <Eyebrow>{territories.filter((t) => t.currentState === 'black').length} teritorií</Eyebrow>
          <Eyebrow>
            {territories.filter((t) => t.currentState === 'neutral').length} neutrálních
          </Eyebrow>
          <Eyebrow>{territories.filter((t) => t.currentState === 'white').length} teritorií</Eyebrow>
        </Group>
      </Box>
    </Frame>
  );
}

function HostinaSummary({ gameUuid }: { gameUuid: string }) {
  const q = useQuery({
    queryKey: ['hostina', gameUuid],
    queryFn: () => gamesApi.getHostinaLeaderboard(gameUuid),
    refetchInterval: 20_000,
  });
  if (q.isLoading) return <Frame title="Průběžné skóre"><Center h={50}><Loader size="xs" /></Center></Frame>;
  if (q.error) return null;
  if (!q.data?.length) {
    return <Frame title="Průběžné skóre"><Empty text="Zatím nikdo nic nenaskenoval." /></Frame>;
  }
  const rows: LeaderboardRow[] = q.data.slice(0, TOP).map((h) => ({
    key: h.groupId, name: h.groupName, value: h.totalPoints, color: h.groupColor,
    sub: plural(h.scannedCount, 'sken', 'skeny', 'skenů'),
  }));
  return <Frame title="Průběžné skóre"><Leaderboard rows={rows} /></Frame>;
}

function PocitadloSummary({ gameUuid }: { gameUuid: string }) {
  const q = useQuery({
    queryKey: ['pocitadlo', gameUuid],
    queryFn: () => gamesApi.getPocitadloLeaderboard(gameUuid),
    refetchInterval: 20_000,
  });
  if (q.isLoading) return <Frame title="Průběžné skóre"><Center h={50}><Loader size="xs" /></Center></Frame>;
  if (q.error) return null;
  if (!q.data?.length) {
    return <Frame title="Průběžné skóre"><Empty text="Zatím nejsou zapsané body." /></Frame>;
  }
  const rows: LeaderboardRow[] = q.data.slice(0, TOP).map((p) => ({
    key: p.group_id, name: p.group_name, value: p.total_points, color: p.group_color,
    sub: plural(p.transaction_count, 'zápis', 'zápisy', 'zápisů'),
  }));
  return <Frame title="Průběžné skóre"><Leaderboard rows={rows} /></Frame>;
}

function StopkySummary({ gameUuid }: { gameUuid: string }) {
  const q = useQuery({
    queryKey: ['stopky', gameUuid],
    queryFn: () => gamesApi.getStopkyTimes(gameUuid),
    refetchInterval: 20_000,
  });
  if (q.isLoading) return <Frame title="Nejlepší časy"><Center h={50}><Loader size="xs" /></Center></Frame>;
  if (q.error) return null;

  // Napříč zařízeními bere každá skupina svůj nejlepší čas — na přehledu stačí pořadí.
  const best = new Map<number, { name: string; color: string; ms: number }>();
  for (const device of q.data ?? []) {
    for (const g of device.groups) {
      const prev = best.get(g.groupId);
      if (!prev || g.elapsedMilliseconds < prev.ms) {
        best.set(g.groupId, { name: g.groupName, color: g.groupColor, ms: g.elapsedMilliseconds });
      }
    }
  }
  const rows = Array.from(best.entries())
    .sort((a, b) => a[1].ms - b[1].ms)
    .slice(0, TOP)
    .map(([id, v]) => ({
      key: id, name: v.name, value: v.ms, display: formatDuration(v.ms), color: v.color,
    }));

  if (rows.length === 0) {
    return <Frame title="Nejlepší časy"><Empty text="Zatím nikdo neměřil čas." /></Frame>;
  }
  return <Frame title="Nejlepší časy"><Leaderboard rows={rows} showBars={false} /></Frame>;
}

function MrtvolisteSummary({ gameUuid }: { gameUuid: string }) {
  const q = useQuery({
    queryKey: ['scores', gameUuid],
    queryFn: () => gamesApi.getScores(gameUuid),
    refetchInterval: 20_000,
  });
  if (q.isLoading) return <Frame title="Nejčastěji padli"><Center h={50}><Loader size="xs" /></Center></Frame>;
  if (q.error) return null;
  if (!q.data?.length) {
    return <Frame title="Nejčastěji padli"><Empty text="Zatím nikdo nezemřel." /></Frame>;
  }

  const rows = q.data.slice(0, TOP).map((s) => ({
    key: s.member_number,
    name: `${s.member_number} · ${s.name ?? 'Bez jména'}`,
    sub: `${s.group_name ?? 'bez skupiny'} · ${s.total_minutes} min mrtvý`,
    value: s.death_count,
    color: s.group_color,
  }));
  return <Frame title="Nejčastěji padli"><Leaderboard rows={rows} unit="×" /></Frame>;
}
